import {
  Box,
  BoxProps,
  Divider,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { dividerStyles, SidePanelStyles } from '@client/style/style.constants';
import { makeStyles } from 'tss-react/mui';
import { createContext, forwardRef, useCallback, useContext } from 'react';
import { ArrayOrSingle } from '@shared/models/type-helpers.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { InsetPanel } from './InsetPanel';
import { PANEL_CONTENT_DEFAULT_SPACING } from './panel.constants';
import { mergeSx } from '@client/helpers/mui.helpers';

const DEFAULT_SPACING = PANEL_CONTENT_DEFAULT_SPACING;

const sectionPaddingSx = {
  pr: DEFAULT_SPACING,
  pl: DEFAULT_SPACING,
} as const;

const useSidePanelContentStyles = makeStyles<{
  disabled?: boolean;
  gap: number;
}>()((theme, { disabled, gap }) => ({
  root: {
    paddingTop: theme.spacing(gap),
    paddingBottom: theme.spacing(gap),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(gap),
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
  },
}));

const ContentContext = createContext<{ rendered?: boolean }>({
  rendered: false,
});
const SidePanelContent: React.FC<{
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  gap?: number;
}> = ({ children, className, disabled, gap = DEFAULT_SPACING }) => {
  const { classes, cx } = useSidePanelContentStyles({ disabled, gap });
  return (
    <ContentContext.Provider value={{ rendered: true }}>
      <Box className={cx(classes.root, className)}>{children}</Box>
    </ContentContext.Provider>
  );
};

const SectionContext = createContext<{ rendered?: boolean }>({
  rendered: false,
});
type SidePanelContentSectionProps = Pick<
  BoxProps,
  'children' | 'onBlur' | 'sx'
>;

const SidePanelContentSection = forwardRef<
  HTMLDivElement,
  SidePanelContentSectionProps
>(({ children, onBlur, sx }, ref) => {
  return (
    <SectionContext.Provider value={{ rendered: true }}>
      <Box ref={ref} onBlur={onBlur} sx={mergeSx(sectionPaddingSx, sx)}>
        {children}
      </Box>
    </SectionContext.Provider>
  );
});
SidePanelContentSection.displayName = 'SidePanelContentSection';

type SidePanelSectionHeaderProps = {
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  rightContent?: React.ReactNode;
};

const SidePanelSectionHeader: React.FC<SidePanelSectionHeaderProps> = ({
  title,
  subtitle,
  rightContent = null,
}) => {
  const titleContent = guards.is.string(title) ? (
    <Typography variant="h4">{title}</Typography>
  ) : (
    title
  );

  const subtitleContent = guards.is.string(subtitle) ? (
    <Typography variant="subtitle3">{subtitle}</Typography>
  ) : (
    subtitle
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        pt: DEFAULT_SPACING,
        pb: DEFAULT_SPACING,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {titleContent}
        {subtitleContent}
      </Box>
      {rightContent}
    </Box>
  );
};

const SidePanelContentDivider: React.FC = () => {
  const contentCtx = useContext(ContentContext);
  const sectionCtx = useContext(SectionContext);
  if (!contentCtx.rendered) {
    console.warn('SidePanelContentDivider rendered without a parent Content');
  }
  const location = sectionCtx.rendered ? 'inner' : 'outer';

  const sx = useCallback(() => {
    switch (location) {
      case 'inner':
        return {
          ...dividerStyles,
          mx: -DEFAULT_SPACING, // hack... but will stretch inner dividers to fill the SidePanelContent, even when inside a Section
        };
      case 'outer':
        return dividerStyles;
    }
  }, [location]);
  return <Divider sx={sx} />;
};

interface SidePanelDrawerProps {
  position?: 'left' | 'right';

  /**
   * Whether to show the side panel.
   */
  show?: boolean;

  /**
   * The width of the side panel.
   */
  width?: number;

  /**
   * Accessible name of the panel. Distinguishes Design's storeys/results
   * asides from the Build element panel for screen readers and tests.
   */
  'aria-label'?: string;

  /** Optional click handler for empty panel surfaces. */
  onClick?: BoxProps['onClick'];
  sx?: BoxProps['sx'];

  /**
   * The children to display in the side panel.
   */
  children?: ArrayOrSingle<React.ReactNode> | null;
}

/**
 * Used to position the side panel on the left or right of the screen.
 * @param children - The children to display in the side panel.
 * @param show - Whether to show the side panel.
 * @returns
 */
const SidePanelComponent: React.FC<SidePanelDrawerProps> = ({
  children,
  show = true,
  width = SidePanelStyles.WIDTH,
  'aria-label': ariaLabel,
  onClick,
  sx,
}) => {
  return (
    <Box
      component="aside"
      aria-label={ariaLabel}
      onClick={onClick}
      sx={mergeSx(useBoxSX(show, width), sx)}
    >
      {children}
    </Box>
  );
};

/**
 * Returns the sx props for the side panel drawer.
 * @param show
 * @param width
 * @param position
 * @returns The sx props function for the side panel drawer.
 */
const useBoxSX = (show: boolean, width: number): SxProps<Theme> =>
  useCallback(() => {
    return {
      width: show ? width : 0,
      flex: '1 1 0',
      overflow: 'hidden auto',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1,
      '@media print': {
        display: 'none',
      },
    };
  }, [show, width]);

/**
 * A side panel component used to position the side panel on the left or right of the screen.
 * It includes a drawer, a content container, and a section container.
 * @returns The side panel component.
 */
export const SidePanel = Object.assign(SidePanelComponent, {
  Content: Object.assign(SidePanelContent, {
    Section: Object.assign(SidePanelContentSection, {
      Header: SidePanelSectionHeader,
    }),
    Divider: SidePanelContentDivider,
    InsetPanel,
  }),
});
